import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, TextField } from '@nyce/admin-kit/ui';
import { ListField, Page, useAsync } from '../../components/common';
import { TargetShell } from '../../identity/TargetShell';
import { activeTarget, authClientFor } from '../../identity/session';
import { setupFor, type ProvisionReport, type ConnectionTestResult } from '../../buddy/api/setup';
import { SCOPES } from '../../api';
import { formatRelative } from '../../lib/format';
import { targetOf, useOnboardingConfig } from '../api/config';
import { addScopes, missingScopes } from '../api/auth';
import { slug } from '../api/directory';
import { buddyFiles } from '../generate/buddy';
import { workServerFiles } from '../generate/workserver';
import { guidePath } from '../guides';
import { Wizard, type WizardStep } from '../wizard/Wizard';
import { FileOutput } from '../wizard/FileOutput';
import { useWizardParam } from '../wizard/params';
import { PLACEHOLDER, rememberSecret, useSecrets } from '../wizard/secrets';
import { Facts, NeedsScope, Note, SecretValue, StepActions, useAction } from '../wizard/parts';

const BUDDY_SCOPES = ['nycebuddy:ask', 'nycebuddy:mcp', 'offline_access'];

/** 24 characters from a URL-safe alphabet: the service user's password. */
export function generatePassword(length = 24): string {
  const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-_';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => abc[b % abc.length]).join('');
}

const workserverBase = (url: string | undefined) => (url ?? '').replace(/\/api\/authenticate\/?$/i, '');

/** W5: nyceBuddy3 for one installation — AUTH claims, organization, tenant, system, connection, provisioning, the
 *  client add-in and MCP. */
export function BuddyWizardPage() {
  return (
    <Page
      title="nyceBuddy"
      description="Give one installation nyceBuddy: the AUTH tenant's scopes, the buddy organization (customer) and tenant (installation), a system, a connection and schema check, the WMS objects, then the add-in and MCP for the customer."
    >
      <TargetShell service="buddy">
        <BuddyWizard />
      </TargetShell>
    </Page>
  );
}

function BuddyWizard() {
  const target = activeTarget();
  const setup = useMemo(() => setupFor(target), [target]);
  const auth = authClientFor(target);
  const config = useOnboardingConfig();
  const env = targetOf(config, target);
  const secrets = useSecrets();
  const [tenantId, setTenantId] = useWizardParam('tenant');
  const [systemParam, setSystemParam] = useWizardParam('system');
  const [tenantForm, setTenantForm] = useState({
    environment: 'prod',
    baseUrl: '',
    serviceUser: 'NYCEBUDDY_SVC',
    logNamespace: '',
    limit: '',
  });
  const [systemForm, setSystemForm] = useState({
    id: '',
    name: '',
    allowedGroups: ['NYCEBUDDY'],
    adminGroups: ['NYCEBUDDY_SYSTEMADMIN'],
    origins: [] as string[],
  });
  const [test, setTest] = useState<ConnectionTestResult | null>(null);
  const [preview, setPreview] = useState<ProvisionReport | null>(null);

  const state = useAsync(async () => {
    const [authTenants, orgs, tenants, systems, dbServers] = await Promise.all([
      setup.authTenants(),
      setup.organizations.list(),
      setup.tenants.list(),
      setup.systems.list(),
      setup.dbServers.list().catch(() => []),
    ]);
    const authTenant = tenantId ? await auth.tenants.get(tenantId).catch(() => null) : null;
    const system = systems.find((x) => x.id === systemParam) ?? systems.find((x) => x.tenantId === tenantId);
    const runs = system ? await setup.systems.provisionRuns(system.id).catch(() => []) : [];
    return { authTenants, orgs, tenants, systems, dbServers, authTenant, system, runs };
  }, [setup, tenantId, systemParam]);
  const action = useAction(state.reload);
  const s = state.data;
  const authTenant = s?.authTenant;
  const missing = missingScopes(authTenant?.claims, BUDDY_SCOPES);
  const org = s?.orgs.find((o) => o.id === authTenant?.customerId);
  const tenant = s?.tenants.find((t) => t.id === tenantId);
  const system = s?.system;
  const lastRun = s?.runs[0];
  const sk = (k: string) => `buddy:${target}:${tenantId}:${k}`;
  const password = secrets(sk('svc-password'));
  const serviceKey = secrets(`tenant:${target}:${tenantId}:service-key`);
  const baseUrl = tenantForm.baseUrl || workserverBase(authTenant?.identityProviderUrl);

  const steps: WizardStep[] = [
    {
      id: 'auth',
      title: 'AUTH tenant and scopes',
      state: !authTenant ? 'todo' : missing.length ? 'todo' : 'done',
      summary: authTenant ? `${authTenant.name} (${authTenant.customerName ?? ''})` : undefined,
      body: (
        <>
          <p>
            Pick the installation's AUTH tenant (one per WorkServer; create it with the{' '}
            <Link className="nyk-link" to="/onboarding/tenant">
              Customer & tenant wizard
            </Link>
            ). Its users need <code>nycebuddy:ask</code>; <code>nycebuddy:mcp</code> and{' '}
            <code>offline_access</code> for MCP clients.
          </p>
          <Select
            label="AUTH tenant"
            value={tenantId}
            onChange={(v) => {
              setTenantId(v);
              setSystemParam('');
            }}
            searchable
            options={[
              { value: '', label: '— pick —' },
              ...(s?.authTenants ?? []).map((t) => ({
                value: t.id,
                label: [t.customerName || t.name || t.id, t.identityProviderUrl].filter(Boolean).join(' — '),
              })),
            ]}
          />
          {authTenant && <Facts rows={[['Missing scopes', missing.join(', ') || 'none']]} />}
          <StepActions error={action.error}>
            <NeedsScope scope={SCOPES.authWrite}>
              <Button
                disabled={!authTenant || !missing.length}
                loading={action.busy === 'scopes'}
                onClick={() => void action.run('scopes', () => addScopes(target, tenantId, missing))}
              >
                Add {missing.join(', ') || 'scopes'}
              </Button>
            </NeedsScope>
          </StepActions>
        </>
      ),
    },
    {
      id: 'organization',
      title: 'Organization',
      state: !authTenant ? 'blocked' : org ? 'done' : 'todo',
      blocked: 'Pick the AUTH tenant first.',
      summary: org?.name,
      body: (
        <>
          <p>
            The buddy organization is the AUTH customer (<b>{authTenant?.customerName}</b>): it carries the
            budget, the statistics and the billing. Names starting with the internal prefix are not billed.
          </p>
          {org ? (
            <Facts
              rows={[
                ['Organization', `${org.name} (${org.id})`],
                [
                  'Monthly limit',
                  org.isUnlimited ? 'unlimited' : (org.monthlyLimit?.toLocaleString() ?? 'default'),
                ],
              ]}
            />
          ) : null}
          <div className="nyk-form">
            <TextField
              label="Monthly NYCE token limit"
              inputMode="numeric"
              value={tenantForm.limit}
              onChange={(e) => setTenantForm({ ...tenantForm, limit: e.target.value.replace(/\D/g, '') })}
              hint="Optional; empty keeps the default."
            />
          </div>
          <StepActions error={action.error}>
            {!org && (
              <Button
                loading={action.busy === 'org'}
                onClick={() =>
                  void action.run('org', async () => {
                    await setup.organizations.create({
                      id: authTenant!.customerId,
                      name: authTenant!.customerName ?? authTenant!.name,
                      enabled: true,
                    });
                    if (tenantForm.limit)
                      await setup.limits.put('organization', authTenant!.customerId, {
                        monthlyLimit: Number(tenantForm.limit),
                        warnPercent: 80,
                      });
                  })
                }
              >
                Create organization
              </Button>
            )}
            {org && tenantForm.limit && (
              <Button
                variant="ghost"
                loading={action.busy === 'limit'}
                onClick={() =>
                  void action.run('limit', () =>
                    setup.limits.put('organization', org.id, {
                      monthlyLimit: Number(tenantForm.limit),
                      warnPercent: 80,
                    })
                  )
                }
              >
                Set limit
              </Button>
            )}
          </StepActions>
        </>
      ),
    },
    {
      id: 'tenant',
      title: 'Installation',
      state: !authTenant ? 'blocked' : tenant ? 'done' : 'todo',
      blocked: 'Pick the AUTH tenant first.',
      summary: tenant ? `${tenant.environment ?? ''} · ${tenant.baseUrl ?? ''}` : undefined,
      body: tenant ? (
        <>
          <Facts
            rows={[
              ['WorkServer', tenant.baseUrl ?? '—'],
              [
                'Service user',
                `${tenant.serviceUser ?? '—'}${tenant.hasServicePassword ? ' (password set)' : ''}`,
              ],
              ['Sign-in flow', tenant.loginFlow ?? tenant.loginFlowSetting ?? 'auto'],
              [
                'Database',
                tenant.dbServerId ? `${tenant.dbServerId} / ${tenant.dbName}` : 'none (provision with SQL)',
              ],
            ]}
          />
          {password && <SecretValue label={`Password of ${tenant.serviceUser}`} value={password} />}
          {tenant.secretError && <Note tone="warn">{tenant.secretError}</Note>}
          <StepActions error={action.error}>
            {!!s?.dbServers.length && !tenant.dbServerId && (
              <Link className="nyk-link" to={`/buddy/tenants?target=${target}`}>
                Pick its database server (Buddy → Tenants)
              </Link>
            )}
          </StepActions>
        </>
      ) : (
        <>
          <p>
            nyceBuddy queries the WMS as a service user. The wizard generates its password; create the user
            with it in NYCE.LOGIC (group <code>NYCEBUDDY</code>).
          </p>
          <div className="nyk-form">
            <Select
              label="Environment"
              value={tenantForm.environment}
              onChange={(environment) => setTenantForm({ ...tenantForm, environment })}
              options={['prod', 'test', 'demo'].map((x) => ({ value: x, label: x }))}
            />
            <TextField
              label="WorkServer base URL"
              className="nyk-url"
              value={baseUrl}
              onChange={(e) => setTenantForm({ ...tenantForm, baseUrl: e.target.value })}
            />
            <TextField
              label="Service user"
              value={tenantForm.serviceUser}
              onChange={(e) => setTenantForm({ ...tenantForm, serviceUser: e.target.value })}
            />
            <TextField
              label="Log namespace"
              value={tenantForm.logNamespace}
              onChange={(e) => setTenantForm({ ...tenantForm, logNamespace: e.target.value })}
              hint="GKE namespace, for the query_logs tool."
            />
          </div>
          {serviceKey ? (
            <Note tone="good">The service key issued in the Customer & tenant wizard is stored with it.</Note>
          ) : (
            <Note>
              No service key: sign-in uses the token flow. A WorkServer before 2026.6 needs the exchange flow
              — store the tenant's key on the Buddy → Tenants page.
            </Note>
          )}
          <StepActions error={action.error}>
            <Button
              disabled={!/^https?:\/\/\S+$/.test(baseUrl) || !tenantForm.serviceUser.trim()}
              loading={action.busy === 'tenant'}
              onClick={() =>
                void action.run('tenant', async () => {
                  const pw = generatePassword();
                  await setup.tenants.create({
                    id: tenantId,
                    name: authTenant!.name,
                    environment: tenantForm.environment,
                    baseUrl: baseUrl.replace(/\/+$/, ''),
                    serviceUser: tenantForm.serviceUser.trim(),
                    servicePassword: pw,
                    ...(serviceKey ? { serviceKey } : {}),
                    loginFlowSetting: 'auto',
                    logNamespace: tenantForm.logNamespace.trim() || undefined,
                    enabled: true,
                  });
                  rememberSecret(sk('svc-password'), pw);
                })
              }
            >
              Create installation
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'system',
      title: 'System',
      state: !tenant || !org ? 'blocked' : system ? 'done' : 'todo',
      blocked: 'Create the organization and the installation first.',
      summary: system ? `${system.id}` : undefined,
      body: system ? (
        <Facts
          rows={[
            ['System', `${system.name} (${system.id})`],
            ['Groups', system.allowedGroups.join(', ')],
            ['Admin groups', system.adminGroups.join(', ') || '—'],
            ['Allowed origins', system.allowedOrigins.join(', ') || '—'],
          ]}
        />
      ) : (
        <>
          <p>
            A system registers the installation for the organization: who may ask (WMS groups), which hosts
            list it, which origins may embed the panel. The id goes into <code>nyce.json</code> and the MCP
            setup.
          </p>
          <div className="nyk-form">
            <TextField
              label="Id"
              value={systemForm.id || slug(`${authTenant?.customerName ?? ''}-${tenantForm.environment}`)}
              onChange={(e) => setSystemForm({ ...systemForm, id: slug(e.target.value) })}
              hint="Slug, cannot be changed later."
            />
            <TextField
              label="Name"
              value={systemForm.name || authTenant?.name || ''}
              onChange={(e) => setSystemForm({ ...systemForm, name: e.target.value })}
            />
            <ListField
              label="Allowed groups"
              value={systemForm.allowedGroups}
              onChange={(allowedGroups) => setSystemForm({ ...systemForm, allowedGroups })}
            />
            <ListField
              label="Admin groups"
              value={systemForm.adminGroups}
              onChange={(adminGroups) => setSystemForm({ ...systemForm, adminGroups })}
            />
            <div className="nyk-span2">
              <ListField
                label="Allowed origins"
                value={
                  systemForm.origins.length ? systemForm.origins : tenant?.baseUrl ? [tenant.baseUrl] : []
                }
                onChange={(origins) => setSystemForm({ ...systemForm, origins })}
                hint="Where the client runs (CORS for the embedded panel)."
              />
            </div>
          </div>
          <StepActions error={action.error}>
            <Button
              loading={action.busy === 'system'}
              onClick={() =>
                void action.run('system', async () => {
                  const id =
                    systemForm.id || slug(`${authTenant?.customerName ?? ''}-${tenantForm.environment}`);
                  await setup.systems.create({
                    id,
                    name: systemForm.name || authTenant!.name,
                    tenantId,
                    authTenantId: tenantId,
                    organizationId: org!.id,
                    allowedGroups: systemForm.allowedGroups,
                    adminGroups: systemForm.adminGroups,
                    hostnamePatterns: [],
                    allowedOrigins: systemForm.origins.length
                      ? systemForm.origins
                      : tenant?.baseUrl
                        ? [tenant.baseUrl]
                        : [],
                    features: {},
                    settings: {},
                    enabled: true,
                  });
                  setSystemParam(id);
                })
              }
            >
              Create system
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'connection',
      title: 'Connection and schema',
      state: !system ? 'blocked' : system.schema?.cachedAt ? 'done' : 'todo',
      blocked: 'Create the system first.',
      summary: system?.schema?.cachedAt ? `schema ${formatRelative(system.schema.cachedAt)}` : undefined,
      body: system ? (
        <>
          <p>
            Tests the WorkServer, the AUTH sign-in of the service user and the business info, then reads the
            WMS schema nyceBuddy queries with.
          </p>
          {test && (
            <Facts
              rows={[
                [
                  'WorkServer',
                  test.workserver
                    ? `${test.workserver.ok ? 'ok' : 'failed'} ${test.workserver.version ?? test.workserver.error ?? ''}`
                    : '—',
                ],
                ['AUTH', test.auth ? (test.auth.ok ? 'ok' : (test.auth.error ?? 'failed')) : '—'],
                [
                  'Business info',
                  test.businessinfo
                    ? test.businessinfo.ok
                      ? `${test.businessinfo.entities ?? ''} entities`
                      : (test.businessinfo.error ?? 'failed')
                    : '—',
                ],
              ]}
            />
          )}
          {system.schema?.error && <Note tone="warn">{system.schema.error}</Note>}
          <StepActions error={action.error}>
            <Button
              variant="secondary"
              loading={action.busy === 'test'}
              onClick={() =>
                void action.run('test', async () => setTest(await setup.systems.testConnection(system.id)))
              }
            >
              Test connection
            </Button>
            <Button
              loading={action.busy === 'schema'}
              onClick={() => void action.run('schema', () => setup.systems.refreshSchema(system.id))}
            >
              {system.schema?.refreshing ? 'Refreshing…' : 'Refresh schema'}
            </Button>
          </StepActions>
        </>
      ) : null,
    },
    {
      id: 'provision',
      title: 'WMS objects',
      state: !system ? 'blocked' : lastRun?.ok ? 'done' : 'todo',
      blocked: 'Create the system first.',
      summary: lastRun ? `${lastRun.ok ? 'provisioned' : 'failed'} ${formatRelative(lastRun.at)}` : undefined,
      body: system ? (
        <>
          <p>
            nyceBuddy needs its workflow, the <code>NYCEBUDDY*</code> groups and the menu entry in the WMS.
            With a database server it writes them itself; otherwise give the SQL to the customer's DBA.
            Restart the WorkServer afterwards.
          </p>
          {preview && (
            <Facts
              rows={preview.items.map((i) => [i.label, `${i.state}${i.detail ? ` – ${i.detail}` : ''}`])}
            />
          )}
          {preview?.error && <Note tone="warn">{preview.error}</Note>}
          <StepActions error={action.error}>
            <Button
              variant="secondary"
              disabled={!tenant?.dbServerId}
              title={!tenant?.dbServerId ? 'The installation has no database server.' : undefined}
              loading={action.busy === 'preview'}
              onClick={() =>
                void action.run('preview', async () =>
                  setPreview(await setup.systems.provisionPreview(system.id, {}))
                )
              }
            >
              Preview
            </Button>
            <Button
              disabled={!tenant?.dbServerId || !preview}
              loading={action.busy === 'apply'}
              onClick={() =>
                void action.run('apply', async () => setPreview(await setup.systems.provision(system.id, {})))
              }
            >
              Apply
            </Button>
            <Button
              variant="ghost"
              loading={action.busy === 'sql'}
              onClick={() => void action.run('sql', () => setup.systems.provisioningSql(system.id))}
            >
              Download SQL
            </Button>
          </StepActions>
        </>
      ) : null,
    },
    {
      id: 'client',
      title: 'Add-in, MCP and WorkServer',
      state: !system ? 'blocked' : 'done',
      blocked: 'Create the system first.',
      body:
        system && env?.buddyUrl ? (
          <>
            <ol>
              <li>
                Create the WMS user <b>{tenant?.serviceUser ?? 'NYCEBUDDY_SVC'}</b> in group{' '}
                <code>NYCEBUDDY</code>
                {password ? ' with the password below' : ' with the password stored on the installation'}.
              </li>
              <li>
                Users who may ask go into group <code>NYCEBUDDY</code> (admins:{' '}
                <code>NYCEBUDDY_SYSTEMADMIN</code>).
              </li>
              <li>
                Add the add-in entry to the client's <code>nyce.json</code>, and check the WorkServer's AUTH
                options.
              </li>
              <li>Restart the WorkServer.</li>
            </ol>
            {password && <SecretValue label="Service user password" value={password} />}
            <FileOutput
              zipName={`nycebuddy-${system.id}.zip`}
              files={[
                ...buddyFiles(env.buddyUrl, system.id),
                ...workServerFiles({
                  authServiceUrl: env.authServiceUrl,
                  userSessions: { authority: env.authUrl, serviceKey: serviceKey ?? PLACEHOLDER },
                }),
              ]}
            />
            <Link
              className="nyk-link"
              to={guidePath('buddy-addin', {
                customer: org?.name,
                buddyUrl: env.buddyUrl,
                system: system.id,
              })}
            >
              Guide for the customer
            </Link>
          </>
        ) : null,
    },
  ];

  return (
    <Wizard
      steps={steps}
      loading={state.loading && !s}
      error={state.error}
      onRecheck={() => void state.reload()}
    />
  );
}
